import React, { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { getEntryByPath } from '../../../navigation'
import { getHelpVideo } from '../../../navigation/videos'
import { docsSections, getSectionPages } from '../../../docs/routes/_config'
import { getDocsIndex } from '../../../docs/components/docsSearchIndex'

// Route-aware help: a "?" in the header that opens a drawer with the
// knowledgebase article (and how-to video, when one exists) for the page the
// user is actually on - mapped via the navigation registry's docs field.
// Pages without a mapping get article suggestions instead.

let articleLookup
const getArticle = (articleId) => {
  if (!articleId) return undefined
  if (!articleLookup) {
    articleLookup = new Map()
    docsSections.forEach((section) => {
      getSectionPages(section).forEach((page) => {
        articleLookup.set(`${section.id}/${page.id}`, {
          title: page.title,
          summary: page.summary,
          tags: page.tags ?? [],
          href: `/docs/${section.id}/${page.id}`,
          section: section.title,
        })
      })
    })
  }
  return articleLookup.get(articleId)
}

const ArticleCard = ({ article, onClose }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 1.5,
    }}
  >
    <Stack direction="row" gap={1} alignItems="flex-start">
      <MenuBookRoundedIcon sx={{ color: '#1E7F88', fontSize: 20, mt: 0.25 }} />
      <Box minWidth={0}>
        <Typography fontWeight={700} fontSize="0.9rem">
          {article.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {article.section}
        </Typography>
      </Box>
    </Stack>
    {article.summary && (
      <Typography variant="body2" color="text.secondary" mt={1}>
        {article.summary}
      </Typography>
    )}
    {article.tags?.length > 0 && (
      <Stack direction="row" flexWrap="wrap" gap={0.5} mt={1}>
        {article.tags.slice(0, 5).map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', borderRadius: 1 }}
          />
        ))}
      </Stack>
    )}
    <Button
      component={Link}
      to={article.href}
      onClick={onClose}
      size="small"
      variant="outlined"
      color="contrast"
      endIcon={<OpenInNewRoundedIcon />}
      sx={{ mt: 1.5 }}
    >
      Open full article
    </Button>
  </Box>
)

const HelpButton = () => {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  const entry = getEntryByPath(location.pathname)
  const article = getArticle(entry?.docs?.articleId)
  const video = getHelpVideo(entry?.docs?.videoId)

  // No direct mapping: suggest the closest knowledgebase articles for this
  // page's title and keywords
  const suggestions = useMemo(() => {
    if (!open || article || !entry) return []
    try {
      return getDocsIndex()
        .search(`${entry.title} ${entry.keywords ?? ''}`, {
          prefix: true,
          fuzzy: 0.2,
        })
        .slice(0, 3)
        .map((hit) => ({
          title: hit.title,
          summary: hit.summary,
          section: hit.section,
          tags: [],
          href: `/docs/${hit.sectionID}/${hit.pageId}`,
        }))
    } catch {
      return []
    }
  }, [open, article, entry])

  const close = () => setOpen(false)

  return (
    <>
      <Tooltip title="Help for this page">
        <HelpOutlineRoundedIcon
          onClick={() => setOpen(true)}
          sx={{ color: 'primary.main', cursor: 'pointer' }}
        />
      </Tooltip>
      <Drawer
        anchor="right"
        open={open}
        onClose={close}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 } } }}
      >
        <Box padding={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography fontWeight={800}>
              {entry ? `Help: ${entry.title}` : 'Help'}
            </Typography>
            <IconButton size="small" onClick={close}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <Stack gap={2}>
            {video && (
              <Box>
                <Typography fontWeight={700} fontSize="0.85rem" mb={0.75}>
                  Watch: {video.title}
                </Typography>
                <Box
                  component="video"
                  controls
                  preload="metadata"
                  poster={video.poster}
                  src={video.url}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    backgroundColor: '#000',
                  }}
                />
              </Box>
            )}

            {article ? (
              <ArticleCard article={article} onClose={close} />
            ) : suggestions.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  No guide is linked to this page yet — these articles look
                  closest:
                </Typography>
                {suggestions.map((suggestion) => (
                  <ArticleCard
                    key={suggestion.href}
                    article={suggestion}
                    onClose={close}
                  />
                ))}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No guide is linked to this page yet.
              </Typography>
            )}

            <Divider />
            <Stack direction="row" gap={1}>
              <Button
                component={Link}
                to="/docs"
                onClick={close}
                size="small"
                variant="text"
                color="contrast"
              >
                Browse the knowledgebase
              </Button>
              <Button
                component={Link}
                to="/support"
                onClick={close}
                size="small"
                variant="text"
                color="contrast"
              >
                Contact support
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </>
  )
}

export default HelpButton
